import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { MobilePage, MobilePageHeader, mobile } from '../components/mobile'
import { JobChatPanel } from '../components/job/JobChatPanel'
import { usePermissions } from '../hooks/usePermissions'
import { supabase } from '../lib/supabase'

interface JobChatSummary {
  id: string
  job_number: string
  title: string
}

export function JobChat() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { can } = usePermissions()
  const canJobChat = can('communication.chat')
  const [job, setJob] = useState<JobChatSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fromState =
    location.state && typeof location.state === 'object' && 'from' in location.state
      ? String((location.state as { from?: unknown }).from || '')
      : ''

  useEffect(() => {
    if (!id) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const { data, error: fetchError } = await supabase
          .from('jobs')
          .select('id, job_number, title')
          .eq('id', id)
          .single()
        if (fetchError) throw fetchError
        if (!cancelled) setJob(data as JobChatSummary)
      } catch (e) {
        if (!cancelled) {
          setJob(null)
          setError(e instanceof Error ? e.message : 'Failed to load job')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [id])

  const goBack = () => {
    // Prefer history so Job Details keeps its original return state
    if (window.history.length > 1) {
      navigate(-1)
      return
    }
    if (fromState) {
      navigate(fromState)
      return
    }
    if (id) navigate(`/jobs/details/${id}`)
    else navigate('/jobs')
  }

  if (loading) {
    return (
      <MobilePage>
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </MobilePage>
    )
  }

  return (
    <MobilePage>
      <MobilePageHeader
        title="Job Chat"
        subtitle={job ? `#${job.job_number} · ${job.title}` : undefined}
        onBack={goBack}
        backLabel="Back to job"
        sticky
      />

      <div className={`${mobile.surface} p-4`}>
        {error || !job || !id ? (
          <p className="text-sm text-gray-500">{error || 'Job not found.'}</p>
        ) : canJobChat ? (
          <JobChatPanel jobId={id} tall />
        ) : (
          <p className="text-sm text-gray-500">You do not have permission to use job chat.</p>
        )}
      </div>
    </MobilePage>
  )
}
